import { useRouter } from 'next/router';
import { format } from 'date-fns';

import { Box, Button, Divider, Icon, Text } from '@zillow/constellation-10';
import { IconArrowRightOutline, IconDownloadFilled } from '@zillow/constellation-icons';

import { useQuery } from '@zg-rentals/react-query5';
import { defaultPaths as paths } from '@zg-rentals/rental-manager-env';
import { DailyStatsBreakdownTable, PortfolioPerformanceInsights } from '@zg-rentals/rental-revenue-components';
import { RentalManagerUserAnalyticsPropertiesWebApiHandler_getListingPerformanceDailyBreakdownV2 } from '@zg-rentals/ts-rental-manager-api-client';
import { csvTool, downloadTextData } from '@zg-rentals/web-base';
import { DEFAULT_ERROR_MESSAGE } from '@zg-rentals/zillow-rental-manager/constants';

import { useGetListingDetails } from '../../hooks/queries/properties';

import type {
  CustomerAnalyticsEnterpriseDailyAnalyticsDto,
  CustomerAnalyticsListingPerformanceDailyBreakdownDtoV2,
  GetUserPropertyDetailsResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

const transformResponse = (data: CustomerAnalyticsListingPerformanceDailyBreakdownDtoV2) => {
  const dailyBreakdowns = data?.dailyBreakdowns.map((breakdown) => ({
    ...breakdown,
    reportDate: new Date(breakdown.reportDate.year, breakdown.reportDate.month - 1, breakdown.reportDate.day),
  }));

  return {
    insights: data?.insights,
    dailyBreakdowns,
  };
};

export const useGetListingPerformanceDailyBreakdownReport = (
  startDay: Date,
  endDay: Date,
  listing?: GetUserPropertyDetailsResponseWebDto,
) => {
  const queryHook = useQuery({
    queryKey: [
      'getListingPerformanceDailyBreakdownReport',
      listing?.listingAlias,
      format(new Date(startDay), 'yyyyMMdd'),
      format(new Date(endDay), 'yyyyMMdd'),
    ],
    queryFn: () => {
      if (!listing?.listingAlias) throw new Error('listingAlias is required');
      return RentalManagerUserAnalyticsPropertiesWebApiHandler_getListingPerformanceDailyBreakdownV2({
        startDayYyyyMmDd: format(new Date(startDay), 'yyyyMMdd'),
        endDayYyyyMmDd: format(new Date(endDay), 'yyyyMMdd'),
        listingAlias: listing.listingAlias,
        includeMarketInsights: true,
      });
    },
    enabled: Boolean(listing?.listingAlias),
    select: transformResponse,
  });
  return queryHook;
};

const MetricsSummaryPage = () => {
  const router = useRouter();

  const listingAlias = router.query.listingAlias as string;
  const endDay = new Date();
  const startDay = new Date(new Date(endDay).setDate(endDay.getDate() - 30));
  const { data: listing } = useGetListingDetails(listingAlias);
  const {
    data: dailyBreakdown,
    isLoading,
    isError,
    error,
  } = useGetListingPerformanceDailyBreakdownReport(startDay, endDay, listing);

  const getDailyBreakdownAsCsv = () => {
    const columnConfig = [
      {
        dataKey: 'reportDate',
        valueGetter: (row: CustomerAnalyticsEnterpriseDailyAnalyticsDto) =>
          format(new Date(row.reportDate.year, row.reportDate.month - 1, row.reportDate.day), 'MM/dd/yyyy'),
        header: 'Date',
      },
      {
        dataKey: 'impressionCount',
        valueGetter: (row: CustomerAnalyticsEnterpriseDailyAnalyticsDto) =>
          typeof row.impressionCount === 'number' ? row.impressionCount.toString() : '-',
        header: 'Total Impressions',
      },
      {
        dataKey: 'viewCount',
        valueGetter: (row: CustomerAnalyticsEnterpriseDailyAnalyticsDto) =>
          typeof row.viewCount === 'number' ? row.viewCount.toString() : '-',
        header: 'Listing Views',
      },
      {
        dataKey: 'leadCount',
        valueGetter: (row: CustomerAnalyticsEnterpriseDailyAnalyticsDto) => row.leadCount.toString(),
        header: 'Leads',
      },
      {
        dataKey: 'phoneLeadCount',
        valueGetter: (row: CustomerAnalyticsEnterpriseDailyAnalyticsDto) => row.phoneLeadCount.toString(),
        header: 'Phone leads',
      },
      {
        dataKey: 'emailLeadCount',
        valueGetter: (row: CustomerAnalyticsEnterpriseDailyAnalyticsDto) => row.emailLeadCount.toString(),
        header: 'Email leads',
      },
      {
        dataKey: 'clickCount',
        valueGetter: (row: CustomerAnalyticsEnterpriseDailyAnalyticsDto) => row.clickCount.toString(),
        header: 'Website referrals',
      },
    ];

    return csvTool(
      columnConfig,
      (dailyBreakdown?.dailyBreakdowns ?? []) as unknown as Array<CustomerAnalyticsEnterpriseDailyAnalyticsDto>,
    );
  };

  const handleExport = () => {
    downloadTextData(getDailyBreakdownAsCsv(), 'Listing_Performance_Export.csv', 'application/octet-stream');
  };

  return (
    <>
      <Box css={{ display: 'flex', justifyContent: 'flex-end', gap: '400' }}>
        {listing?.isPaidMultifamily && (
          <Button
            tone="brand"
            emphasis="filled"
            icon={<Icon size="sm" render={<IconArrowRightOutline />} />}
            iconPosition="end"
            asChild={!isLoading}
            disabled={isLoading}
          >
            {isLoading ? (
              'See full listing performance'
            ) : (
              <a href={`${paths.analyticsPage}/${paths.analyticsListingPerformance}/${listingAlias}`}>
                See full listing performance
              </a>
            )}
          </Button>
        )}
        <Button
          tone="neutral"
          emphasis="outlined"
          icon={<Icon size="sm" render={<IconDownloadFilled />} />}
          iconPosition="start"
          onClick={handleExport}
          disabled={isLoading}
        >
          Export
        </Button>
      </Box>

      {!isError && listing?.isPaidMultifamily && dailyBreakdown?.insights?.portfolio && (
        <>
          <PortfolioPerformanceInsights
            isLoading={isLoading}
            filteredPropertiesReport={{
              portfolioInsights: {
                ...dailyBreakdown.insights.portfolio,
                reviewAverage: 0,
              },
            }}
            isTotalMetrics={true}
            startingDay={startDay}
            endingDay={endDay}
          />
          <Divider />
        </>
      )}

      <Text textStyle="heading-sm" css={{ marginTop: '600', marginBottom: '200' }} asChild>
        <h4>Summary</h4>
      </Text>
      <Text textStyle="body-xs-bold" color="text.subtle" css={{ marginBottom: '800' }} asChild>
        <p>PAST 30 DAYS</p>
      </Text>

      {isError && (
        <Text css={{ color: 'text.onImpact.feedback.critical.hero' }}>{error?.message || DEFAULT_ERROR_MESSAGE}</Text>
      )}
      {!isError && (
        <DailyStatsBreakdownTable
          dailyBreakdown={dailyBreakdown?.dailyBreakdowns ?? []}
          isSingleFamily={!listing?.isMultifamily}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default MetricsSummaryPage;
